import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { FileDown, LogOut, KeyRound, CalendarCheck, Gift, Users, Download } from "lucide-react";
import { Logo } from "@/components/Logo";
import { api } from "@/lib/api";

const TOKEN_KEY = "po_token";
const EMAIL_KEY = "po_email";

const LoginCard = ({ onLogin }) => {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/access/login", { email });
      localStorage.setItem(TOKEN_KEY, data.token);
      localStorage.setItem(EMAIL_KEY, data.email);
      onLogin(data.email);
      toast.success("Bentornata nella tua area riservata");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Nessun acquisto trovato per questa email");
    } finally {
      setLoading(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 32 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
      className="w-full max-w-md rounded-[2rem] border border-ink/10 bg-white p-10 shadow-2xl shadow-espresso/10"
    >
      <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-terracotta/10 text-terracotta">
        <KeyRound className="h-5 w-5" />
      </div>
      <h1 className="mt-6 font-serif text-2xl text-ink sm:text-3xl">Area riservata</h1>
      <p className="mt-3 text-sm leading-relaxed text-taupe">
        Inserisci l'email che hai usato per l'acquisto: ti diamo accesso immediato al workbook e a tutto il percorso.
      </p>
      <form onSubmit={submit} className="mt-8 space-y-4">
        <input
          type="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="la-tua@email.it"
          data-testid="member-login-email-input"
          className="w-full rounded-full border border-ink/15 bg-linen px-6 py-4 text-sm text-ink placeholder:text-stone2 focus:border-terracotta focus:outline-none"
        />
        <button
          type="submit"
          disabled={loading}
          data-testid="member-login-submit-button"
          className="w-full rounded-full bg-terracotta px-6 py-4 text-sm font-semibold text-linen shadow-lg shadow-terracotta/25 transition-all hover:bg-terracotta-dark disabled:opacity-60"
        >
          {loading ? "Verifica in corso…" : "Accedi al mio percorso"}
        </button>
      </form>
      <p className="mt-6 text-center text-xs text-stone2">
        Non hai ancora acquistato?{" "}
        <Link to="/" className="font-semibold text-terracotta hover:underline" data-testid="member-login-back-home">
          Scopri il percorso
        </Link>
      </p>
    </motion.div>
  );
};

const downloadCsv = (leads) => {
  const rows = [
    ["Email", "Data iscrizione", "Fonte"],
    ...leads.map((l) => [l.email, new Date(l.created_at).toLocaleString("it-IT"), l.source || ""]),
  ];
  const csv = rows.map((r) => r.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(",")).join("\n");
  const blob = new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8" });
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = "contatti-prima-ora.csv";
  a.click();
  URL.revokeObjectURL(a.href);
};

const LeadsSection = ({ leads }) => (
  <div data-testid="admin-leads-section" className="mt-20">
    <div className="flex flex-wrap items-center justify-between gap-4">
      <h2 className="flex items-center gap-3 font-serif text-2xl text-ink sm:text-3xl">
        <Users className="h-6 w-6 text-terracotta" /> I tuoi contatti
        <span className="rounded-full bg-terracotta/10 px-3 py-1 font-sans text-sm font-semibold text-terracotta">
          {leads.length}
        </span>
      </h2>
      <button
        onClick={() => downloadCsv(leads)}
        data-testid="admin-leads-download"
        className="flex items-center gap-2 rounded-full border border-ink/15 px-5 py-2.5 text-sm font-semibold text-ink transition-colors hover:border-terracotta hover:text-terracotta"
      >
        <Download className="h-4 w-4" /> Scarica CSV (Excel)
      </button>
    </div>
    <p className="mt-3 text-sm text-taupe">
      Le email di chi ha richiesto il Capitolo 1 gratuito. Contattale o importale nel tuo strumento di newsletter.
    </p>
    <div className="mt-8 overflow-hidden rounded-[1.5rem] border border-ink/8 bg-white">
      {leads.length === 0 ? (
        <p className="p-8 text-center text-sm text-stone2" data-testid="admin-leads-empty">
          Ancora nessun contatto: appena qualcuna scarica il capitolo gratuito, la trovi qui.
        </p>
      ) : (
        <table className="w-full text-left text-sm" data-testid="admin-leads-table">
          <thead>
            <tr className="border-b border-ink/8 bg-sand/60 text-xs uppercase tracking-widest text-stone2">
              <th className="px-6 py-4 font-medium">Email</th>
              <th className="hidden px-6 py-4 font-medium sm:table-cell">Data</th>
              <th className="hidden px-6 py-4 font-medium md:table-cell">Fonte</th>
            </tr>
          </thead>
          <tbody>
            {leads.map((l) => (
              <tr key={l.email} className="border-b border-ink/5 last:border-0">
                <td className="px-6 py-4 font-medium text-ink">{l.email}</td>
                <td className="hidden px-6 py-4 text-taupe sm:table-cell">
                  {new Date(l.created_at).toLocaleString("it-IT", { dateStyle: "medium", timeStyle: "short" })}
                </td>
                <td className="hidden px-6 py-4 text-taupe md:table-cell">{l.source}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  </div>
);

export default function MemberArea() {
  const [email, setEmail] = useState(localStorage.getItem(EMAIL_KEY));
  const [data, setData] = useState(null);
  const [leads, setLeads] = useState(null);
  const [denied, setDenied] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) return;
    api
      .get("/access/content", { headers: { Authorization: `Bearer ${token}` } })
      .then((res) => setData(res.data))
      .catch(() => {
        localStorage.removeItem(TOKEN_KEY);
        setDenied(true);
      });
  }, [email]);

  useEffect(() => {
    if (!data?.is_admin) return;
    const token = localStorage.getItem(TOKEN_KEY);
    api
      .get("/admin/leads", { headers: { Authorization: `Bearer ${token}` } })
      .then((res) => setLeads(res.data.leads))
      .catch(() => setLeads([]));
  }, [data]);

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(EMAIL_KEY);
    setEmail(null);
    setData(null);
    setLeads(null);
  };

  if (!email || denied) {
    return (
      <main className="flex min-h-screen flex-col bg-linen">
        <header className="px-5 py-6 sm:px-8"><Logo /></header>
        <div className="flex flex-1 items-center justify-center px-5 pb-20">
          <LoginCard onLogin={(e) => { setDenied(false); setEmail(e); }} />
        </div>
      </main>
    );
  }

  if (!data) {
    return (
      <main className="flex min-h-screen items-center justify-center bg-linen">
        <p className="font-serif text-xl text-taupe" data-testid="member-loading">Caricamento del tuo percorso…</p>
      </main>
    );
  }

  const { content, workbook_url } = data;

  return (
    <main data-testid="member-area" className="min-h-screen bg-linen">
      <header className="border-b border-ink/10 bg-linen/85 backdrop-blur-xl">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-5 py-4 sm:px-8">
          <Logo />
          <button
            onClick={logout}
            data-testid="member-logout-button"
            className="flex items-center gap-2 rounded-full border border-ink/15 px-5 py-2 text-sm font-semibold text-ink transition-colors hover:border-terracotta hover:text-terracotta"
          >
            <LogOut className="h-4 w-4" /> Esci
          </button>
        </div>
      </header>

      <div className="mx-auto max-w-6xl px-5 py-16 sm:px-8">
        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
          <span className="font-mono text-[11px] uppercase tracking-[0.25em] text-terracotta">Bentornata, {email}</span>
          <h1 className="mt-4 font-serif text-3xl tracking-tight text-ink sm:text-4xl lg:text-5xl">
            Il tuo percorso verso la <em className="italic text-terracotta">prima ora di libertà</em>
          </h1>
          <p className="mt-4 max-w-2xl text-base leading-relaxed text-taupe">{content.intro}</p>
        </motion.div>

        <motion.a
          href={workbook_url}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="member-download-workbook"
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.15 }}
          className="group mt-12 flex flex-col items-start justify-between gap-6 rounded-[2rem] bg-terracotta p-10 transition-all hover:-translate-y-1 hover:shadow-2xl hover:shadow-terracotta/30 sm:flex-row sm:items-center"
        >
          <div>
            <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-linen/80">Il tuo materiale</p>
            <h2 className="mt-3 font-serif text-2xl text-linen sm:text-3xl">Workbook completo · PDF</h2>
            <p className="mt-2 max-w-lg text-sm text-linen/80">
              Quiz, percorso di 7 giorni, checklist compilabili, modelli e piano d'azione di 30 giorni.
            </p>
          </div>
          <span className="flex shrink-0 items-center gap-2 rounded-full bg-linen px-7 py-4 text-sm font-semibold text-terracotta transition-colors group-hover:bg-sand">
            <FileDown className="h-4 w-4" /> Scarica il workbook
          </span>
        </motion.a>

        <h2 className="mt-20 flex items-center gap-3 font-serif text-2xl text-ink sm:text-3xl">
          <CalendarCheck className="h-6 w-6 text-terracotta" /> Le tue 7 giornate
        </h2>
        <div className="mt-10 space-y-4" data-testid="member-days-list">
          {content.days.map((d, i) => (
            <motion.div
              key={d.day}
              initial={{ opacity: 0, x: -24 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: i * 0.05 }}
              className="flex gap-6 rounded-[1.5rem] border border-ink/8 bg-white p-6 transition-shadow hover:shadow-lg hover:shadow-espresso/5 sm:p-8"
            >
              <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-terracotta/10 font-serif text-lg text-terracotta">
                {d.day}
              </div>
              <div>
                <h3 className="font-serif text-lg text-ink sm:text-xl">Giorno {d.day} · {d.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-taupe">{d.text}</p>
              </div>
            </motion.div>
          ))}
        </div>

        <h2 className="mt-20 flex items-center gap-3 font-serif text-2xl text-ink sm:text-3xl">
          <Gift className="h-6 w-6 text-terracotta" /> Strumenti inclusi
        </h2>
        <div className="mt-10 grid gap-5 sm:grid-cols-2">
          {content.bonus.map((b) => (
            <div key={b.title} className="rounded-[1.5rem] border border-ink/8 bg-sand/70 p-8">
              <h3 className="font-serif text-lg text-ink">{b.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-taupe">{b.text}</p>
            </div>
          ))}
        </div>

        {data.is_admin && leads !== null && <LeadsSection leads={leads} />}
      </div>
    </main>
  );
}
